import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import Button from "../component-lib/Molecules/button/Button";
import { CUSTOM_lABEL_STYLE } from "../screens/patient-screen/visit-flow/constant";
import Heading from "../component-lib/Atoms/heading/EventEHRHeading";
import useDeviceType from "../custom-hooks/DeviceType";
import SelectDropdown from "../component-lib/Atoms/Select/Select";
import { TYPE_OF_TRANSFER_DISPOSITION_DATA } from "./constants";
import { useDispatch, useSelector } from "react-redux";
import { getFacilities } from "../screens/patient-screen/patientSaga";
import {
  componentKey,
  setBundleOrderData,
} from "../screens/patient-screen/patientSlice";
import BundleOrderModal from "../screens/patient-screen/visit-flow/dialogs/BundleOrderModal";
import BundleOrderDisplay from "../screens/patient-screen/visit-flow/dialogs/BundelOrderDisplay";

interface TransferToJHCFormProps {
  getFormData: (data: any) => void;
}

export default function TransferToJHCForm({
  getFormData,
}: TransferToJHCFormProps) {
  const { facilityData } = useSelector((state: any) => state[componentKey]);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);

  // Any facility can now receive the transfer except "Offsite Transfers"
  // (which has its own disposition). The chosen facility's departments drive
  // the Department (Type of Care) dropdown.
  const facilityOptions =
    facilityData
      ?.filter(
        (data: any) => !data?.name?.toLowerCase()?.includes("offsite")
      )
      ?.map((data: any) => ({
        label: data?.name,
        value: data?.id,
      })) || [];

  const dispatch = useDispatch();
  const deviceType = useDeviceType();

  const validationSchema = Yup.object().shape({
    [TYPE_OF_TRANSFER_DISPOSITION_DATA?.TRANSFER_FACILITY]:
      Yup.string().required("Facility is required"),
    [TYPE_OF_TRANSFER_DISPOSITION_DATA?.JAMBOREE_HEALTH_CENTER]:
      Yup.string().required("Department is required"),
  });

  useEffect(() => {
    try {
      dispatch(getFacilities());
    } catch (error) {
      console.log("error", error);
    }
  }, []);

  return (
    <>
      <Formik
        initialValues={{
          [TYPE_OF_TRANSFER_DISPOSITION_DATA?.TRANSFER_FACILITY]: "",
          [TYPE_OF_TRANSFER_DISPOSITION_DATA?.JAMBOREE_HEALTH_CENTER]: "",
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            getFormData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          isValid,
          dirty,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          // Departments come from the currently selected receiving facility.
          const selectedFacility = facilityData?.find(
            (data: any) =>
              data?.id ===
              values[TYPE_OF_TRANSFER_DISPOSITION_DATA.TRANSFER_FACILITY]
          );
          const departmentList =
            selectedFacility?.department?.map((data: any) => ({
              label: data?.name,
              value: data?.id,
            })) || [];
          return (
            <Form onSubmit={handleSubmit}>
              <div
                className="h-full flex flex-col justify-between gap-6"
                style={{
                  height: deviceType === "mobile" ? "40vh" : "fit-content",
                  overflow: "auto",
                }}
              >
                {/* FACILITY DROPDOWN */}
                <div>
                  <Heading title="Facility" customStyle={CUSTOM_lABEL_STYLE} />
                  <SelectDropdown
                    label=""
                    displayEmpty={true}
                    placeholderValue={"Select Facility"}
                    value={
                      values[
                        TYPE_OF_TRANSFER_DISPOSITION_DATA.TRANSFER_FACILITY
                      ]
                    }
                    options={facilityOptions}
                    onChangeCb={(e: any) => {
                      setFieldValue(
                        TYPE_OF_TRANSFER_DISPOSITION_DATA.TRANSFER_FACILITY,
                        e.target.value
                      );
                      // Reset the department when the facility changes.
                      setFieldValue(
                        TYPE_OF_TRANSFER_DISPOSITION_DATA.JAMBOREE_HEALTH_CENTER,
                        ""
                      );
                    }}
                    error={
                      touched[
                        TYPE_OF_TRANSFER_DISPOSITION_DATA.TRANSFER_FACILITY
                      ] &&
                      Boolean(
                        errors[
                          TYPE_OF_TRANSFER_DISPOSITION_DATA.TRANSFER_FACILITY
                        ]
                      )
                    }
                    helperText={
                      touched[
                        TYPE_OF_TRANSFER_DISPOSITION_DATA.TRANSFER_FACILITY
                      ]
                        ? errors[
                            TYPE_OF_TRANSFER_DISPOSITION_DATA.TRANSFER_FACILITY
                          ]
                        : undefined
                    }
                  />
                </div>
                {/* DEPARTMENT DROPDOWN */}
                <div>
                  <Heading title="Department" customStyle={CUSTOM_lABEL_STYLE} />
                  <SelectDropdown
                    label=""
                    displayEmpty={true}
                    placeholderValue={"Select Department"}
                    value={
                      values[
                        TYPE_OF_TRANSFER_DISPOSITION_DATA.JAMBOREE_HEALTH_CENTER
                      ]
                    }
                    options={departmentList}
                    onChangeCb={(e: any) =>
                      setFieldValue(
                        TYPE_OF_TRANSFER_DISPOSITION_DATA.JAMBOREE_HEALTH_CENTER,
                        e.target.value
                      )
                    }
                    error={
                      touched[
                        TYPE_OF_TRANSFER_DISPOSITION_DATA.JAMBOREE_HEALTH_CENTER
                      ] &&
                      Boolean(
                        errors[
                          TYPE_OF_TRANSFER_DISPOSITION_DATA
                            .JAMBOREE_HEALTH_CENTER
                        ]
                      )
                    }
                    helperText={
                      touched[
                        TYPE_OF_TRANSFER_DISPOSITION_DATA.JAMBOREE_HEALTH_CENTER
                      ]
                        ? errors[
                            TYPE_OF_TRANSFER_DISPOSITION_DATA
                              .JAMBOREE_HEALTH_CENTER
                          ]
                        : undefined
                    }
                  />
                </div>
              </div>
              <BundleOrderDisplay/>
              {/* ACTION BUTTONS */}
              <div className="mt-9 text-end flex gap-2 justify-end">
                {/* <Button
                  type="button"
                  label="Create Bundle Order"
                  variant="outlined"
                  onClickCb={() => {
                    setBundleModalOpen(true);
                  }}
                  style={{
                    padding: "8px 24px",
                    fontSize: "16px",
                    borderColor: "#1976d2",
                    color: "#1976d2",
                    backgroundColor: "white",
                    marginRight: "12px",
                  }}
                /> */}
                <Button
                  // disabled={!isValid} // Button disabled if form is invalid or submitting
                  type="submit"
                  label="Submit"
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    padding: "8px 55px",
                    fontSize: "16px",
                  }}
                ></Button>
              </div>
            </Form>
          );
        }}
      </Formik>

      <BundleOrderModal
        open={bundleModalOpen}
        handleClose={() => setBundleModalOpen(false)}
        handleSave={(data) => {
          console.log("Bundle Order Data:", data);
          dispatch(setBundleOrderData(data));
          setBundleModalOpen(false);
        }}
      />
    </>
  );
}
